import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Save, Wallet } from '@/lib/icons'
import type { MonthlyBudgetStatus } from '@ayub/shared'
import { formatSom } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrentAdmin } from '@/components/settings/hooks'
import { formatIntegerInput, parseIntegerInput } from '@/components/settings/numericInput'
import { ApiError } from '@/lib/api'
import { fetchBudgetStatus, setBudget } from './budgetApi'
import { useBudget } from './hooks'

const MONTH_NAMES_UZ = [
  'Yanvar',
  'Fevral',
  'Mart',
  'Aprel',
  'May',
  'Iyun',
  'Iyul',
  'Avgust',
  'Sentabr',
  'Oktabr',
  'Noyabr',
  'Dekabr',
]

function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES_UZ[month - 1] ?? month} ${year}`
}

/** (year, month) ga `delta` oy qo'shadi (oy 1–12). */
function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta
  return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

/**
 * To'lovlar sahifasi tepasidagi oylik byudjet paneli — joriy oy Byudjet/To'langan/Qolgan va
 * to'lanmagan qoldiq (backlog). Super admin joriy va keyingi oy byudjetini o'rnatadi/tahrirlaydi.
 * Byudjet — qattiq cheklov: server bu oyda byudjetdan ortiq to'lashni bloklaydi (`markPayoutPaid`).
 */
export function BudgetPanel() {
  const currentAdmin = useCurrentAdmin()
  const { budget, isLoading } = useBudget()

  if (isLoading || !budget) return <Skeleton className="h-28" />

  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
          <Wallet size={16} className="text-brand" aria-hidden="true" />
          Oylik byudjet — {monthLabel(budget.year, budget.month)}
        </h2>
      </div>

      <BudgetStats budget={budget} />

      {currentAdmin.isSuper && <BudgetEditor anchor={{ year: budget.year, month: budget.month }} />}
    </section>
  )
}

function BudgetStats({ budget }: { budget: MonthlyBudgetStatus }) {
  const overspent = budget.remaining !== null && budget.remaining < 0
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat
        label="Byudjet"
        value={budget.amount === null ? "O'rnatilmagan" : formatSom(budget.amount)}
        muted={budget.amount === null}
      />
      <Stat label="To'langan" value={formatSom(budget.spent)} />
      <Stat
        label="Qolgan"
        value={budget.remaining === null ? '—' : formatSom(budget.remaining)}
        tone={budget.remaining === null ? 'default' : overspent ? 'danger' : 'good'}
      />
      <Stat
        label="To'lanishi kerak (jami)"
        value={formatSom(budget.outstanding)}
        hint="barcha blogerlar hisobidagi to'lanmagan summa"
      />
    </dl>
  )
}

function Stat({
  label,
  value,
  hint,
  tone = 'default',
  muted = false,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'default' | 'good' | 'danger'
  muted?: boolean
}) {
  const valueClass =
    tone === 'danger'
      ? 'text-stage-rejected-text'
      : tone === 'good'
        ? 'text-urgency-done-text'
        : muted
          ? 'text-app-hint'
          : 'text-app-text'
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-app-hint">{label}</dt>
      <dd className={`tabular text-sm font-semibold ${valueClass}`}>{value}</dd>
      {hint && <span className="text-[11px] text-app-hint">{hint}</span>}
    </div>
  )
}

/** Super admin uchun — joriy yoki keyingi oy byudjetini o'rnatish/tahrirlash. */
function BudgetEditor({ anchor }: { anchor: { year: number; month: number } }) {
  const queryClient = useQueryClient()

  const monthOptions = [0, 1, 2].map((delta) => addMonths(anchor.year, anchor.month, delta))
  const [selectedIndex, setSelectedIndex] = useState(0)
  const target = monthOptions[selectedIndex] ?? monthOptions[0]!

  // Tanlangan oyning joriy byudjeti — inputni oldindan to'ldirish uchun.
  const targetQuery = useQuery<MonthlyBudgetStatus, Error>({
    queryKey: ['budget', 'status', target.year, target.month],
    queryFn: () => fetchBudgetStatus({ year: target.year, month: target.month }),
  })

  const [amountRaw, setAmountRaw] = useState('')
  // Tanlangan oy o'zgarsa yoki uning byudjeti yuklansa — inputni o'sha oy qiymati bilan to'ldiramiz.
  useEffect(() => {
    const amount = targetQuery.data?.amount
    setAmountRaw(amount === null || amount === undefined ? '' : formatIntegerInput(String(amount)))
  }, [targetQuery.data, target.year, target.month])

  const mutation = useMutation<MonthlyBudgetStatus, Error, number>({
    mutationFn: (amount) => setBudget({ year: target.year, month: target.month, amount }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0]
          return key === 'budget' || key === 'dashboard'
        },
      })
    },
  })

  const parsed = parseIntegerInput(amountRaw)
  const canSave = parsed !== null && parsed >= 0 && !mutation.isPending

  return (
    <div className="flex flex-col gap-2 border-t border-app-border pt-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-app-hint">
          Oy
          <select
            value={selectedIndex}
            onChange={(event) => setSelectedIndex(Number(event.target.value))}
            className="h-11 rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text outline-none focus-visible:border-brand"
          >
            {monthOptions.map((option, index) => (
              <option key={`${option.year}-${option.month}`} value={index}>
                {monthLabel(option.year, option.month)}
                {index === 0 ? ' (joriy)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-1 flex-col gap-1 text-xs text-app-hint">
          Byudjet (so&apos;m)
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={amountRaw}
            onChange={(event) => setAmountRaw(formatIntegerInput(event.target.value))}
            placeholder="masalan: 10 000 000"
            className="tabular h-11 w-full min-w-40 rounded-input border border-app-border bg-app-bg px-3 text-right text-base text-app-text outline-none focus-visible:border-brand"
          />
        </label>

        <Button onClick={() => parsed !== null && mutation.mutate(parsed)} disabled={!canSave}>
          {mutation.isPending ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Save size={16} aria-hidden="true" />
          )}
          Saqlash
        </Button>
      </div>

      {mutation.isError && (
        <p className="text-xs text-stage-rejected-text">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Saqlab bo‘lmadi'}
        </p>
      )}
      {mutation.isSuccess && !mutation.isPending && (
        <p className="text-xs text-urgency-done-text">Byudjet saqlandi.</p>
      )}
      <p className="text-[11px] text-app-hint">
        Byudjet — qattiq cheklov: shu oyda byudjetdan ortiq to‘lov qilib bo‘lmaydi. Ortiqcha
        ishlangan pul blogerlar balansida qoladi va keyingi oy to‘lanadi. Ishlatilmagan byudjet
        keyingi oyga o‘tmaydi.
      </p>
    </div>
  )
}
